// SitePicker — shared component used by Job/Enquiry create/edit forms
// and SkipJobDetailPage's inline edit mode (Phase 6D).
//
// Modes:
//   - 'site'    — pick from the customer's saved sites
//   - 'one_off' — type a one-off `job_address_*` set
//   - 'none'    — nothing selected (initial state when no customer set)
//
// Auto-selects the customer's primary site on first load. Expands an
// inline "Add new site +" sub-form that POSTs to
// /api/v1/customers/:enc_id/sites and re-runs the site list.

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';

import { Button } from '../Button/Button';
import {
  useCreateCustomerSite,
  useCustomerSites,
} from '../../../pages/skip/hooks/useSkipJobs';

import styles from './SitePicker.module.css';

// =================================================================== TYPES

export type SitePickerValue =
  | { type: 'none' }
  | { type: 'site'; site_enc_id: string }
  | {
      type: 'one_off';
      line1: string;
      line2: string | null;
      city: string | null;
      postcode: string;
    };

export interface SitePickerInitialAddress {
  line1: string | null;
  line2: string | null;
  city: string | null;
  postcode: string | null;
}

export interface SitePickerProps {
  customerEncId: string | null;
  /** enc_id of the saved site to pre-select. NOTE: encryptField is
   *  non-deterministic, so the enc_id passed in often won't match
   *  the enc_id strings on the customer's site list (different
   *  ciphertexts of the same uuid). Prefer `initialSiteUuid` below
   *  when both are available — uuid is stable. This prop is kept
   *  for backward compat and is used as a fallback when the parent
   *  doesn't yet have the saved row's uuid. */
  initialSiteEncId?: string | null;
  /** Stable uuid of the saved site to pre-select. The picker matches
   *  this against `s.uuid` on each option, which works across API
   *  responses (unlike enc_id). When both initialSiteUuid and
   *  initialSiteEncId are passed, uuid wins. */
  initialSiteUuid?: string | null;
  initialJobAddress?: SitePickerInitialAddress;
  /** Called whenever the picker's value changes. Debounced for one-off
   *  address fields so callers don't get a torrent of updates while
   *  the user is typing. */
  onChange: (value: SitePickerValue) => void;
}

type Mode = 'site' | 'one_off';

// =================================================================== COMPONENT

export function SitePicker({
  customerEncId,
  initialSiteEncId,
  initialSiteUuid,
  initialJobAddress,
  onChange,
}: SitePickerProps): JSX.Element {
  const sitesQuery = useCustomerSites(customerEncId);
  const sites = useMemo(() => sitesQuery.data?.sites ?? [], [sitesQuery.data]);
  const createSite = useCreateCustomerSite(customerEncId);

  // Initial mode resolves once customer + initial values are known.
  // Edit pages pass either an initialSiteEncId/Uuid (mode='site') or
  // an initialJobAddress (mode='one_off'). Create pages pass nothing →
  // mode='site' with the primary auto-selected.
  const initialMode: Mode = useMemo(() => {
    if (initialSiteUuid || initialSiteEncId) return 'site';
    if (
      initialJobAddress?.line1 ||
      initialJobAddress?.postcode
    ) {
      return 'one_off';
    }
    return 'site';
  }, [initialSiteEncId, initialSiteUuid, initialJobAddress]);

  const [mode, setMode] = useState<Mode>(initialMode);
  // Track the selected site by uuid (stable). The dropdown's <option>
  // value is also the uuid. This sidesteps the encryptField-is-not-
  // deterministic problem — a uuid in initialSiteUuid will match the
  // sites list's uuid even though their enc_id strings differ.
  const [selectedSiteUuid, setSelectedSiteUuid] = useState<string>(
    initialSiteUuid ?? '',
  );
  const [line1, setLine1] = useState(initialJobAddress?.line1 ?? '');
  const [line2, setLine2] = useState(initialJobAddress?.line2 ?? '');
  const [city, setCity] = useState(initialJobAddress?.city ?? '');
  const [postcode, setPostcode] = useState(initialJobAddress?.postcode ?? '');

  const [showAddForm, setShowAddForm] = useState(false);
  const [addLine1, setAddLine1] = useState('');
  const [addLine2, setAddLine2] = useState('');
  const [addCity, setAddCity] = useState('');
  const [addPostcode, setAddPostcode] = useState('');
  const [addLabel, setAddLabel] = useState('');
  const [addError, setAddError] = useState<string | null>(null);

  // ------------------------------------------------- onChange dispatcher

  // Latest-onChange ref so the debounced one-off handler doesn't have to
  // re-create itself when the parent passes a new closure each render.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  function emit(next: SitePickerValue): void {
    onChangeRef.current(next);
  }

  // Debounce one-off field changes (300ms) so each keystroke does not
  // flood the parent. Site selection / mode toggles emit immediately.
  const oneOffTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  function emitOneOffDebounced(
    nextLine1: string,
    nextLine2: string,
    nextCity: string,
    nextPostcode: string,
  ): void {
    if (oneOffTimer.current !== null) {
      clearTimeout(oneOffTimer.current);
    }
    oneOffTimer.current = setTimeout(() => {
      if (!nextLine1.trim() || !nextPostcode.trim()) {
        emit({ type: 'none' });
        return;
      }
      emit({
        type: 'one_off',
        line1: nextLine1.trim(),
        line2: nextLine2.trim() || null,
        city: nextCity.trim() || null,
        postcode: nextPostcode.trim(),
      });
    }, 300);
  }

  useEffect(() => {
    return () => {
      if (oneOffTimer.current !== null) clearTimeout(oneOffTimer.current);
    };
  }, []);

  // ------------------------------------------------- mount + auto-select

  // Emit the initial value once on mount so the parent form picks up
  // pre-existing data (edit pages with initial site or one-off
  // address).
  const mountedRef = useRef(false);
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;
    if (initialSiteEncId) {
      // Edge case — only the (possibly stale) enc_id is known. Emit
      // it so the parent form has the initial value; the visual
      // selection still requires the uuid match below.
      emit({ type: 'site', site_enc_id: initialSiteEncId });
    } else if (
      initialJobAddress &&
      initialJobAddress.line1 &&
      initialJobAddress.postcode
    ) {
      emit({
        type: 'one_off',
        line1: initialJobAddress.line1,
        line2: initialJobAddress.line2,
        city: initialJobAddress.city,
        postcode: initialJobAddress.postcode,
      });
    }
  }, [initialSiteEncId, initialJobAddress]);

  // Once the customer's sites land:
  //   - If initialSiteUuid was passed and the row exists in the list,
  //     pre-select it and emit its fresh enc_id so the parent form's
  //     payload uses a ciphertext the server still recognises.
  //   - Otherwise, fall back to auto-selecting the primary (create-
  //     page path).
  useEffect(() => {
    if (mode !== 'site') return;
    if (selectedSiteUuid) return;
    if (sites.length === 0) return;
    let target = initialSiteUuid
      ? sites.find((s) => s.uuid === initialSiteUuid)
      : undefined;
    if (!target) {
      target = sites.find((s) => s.is_primary) ?? sites[0];
    }
    if (target) {
      setSelectedSiteUuid(target.uuid);
      const enc = target.enc_id ?? target.uuid;
      emit({ type: 'site', site_enc_id: enc });
    }
  }, [sites, mode, selectedSiteUuid, initialSiteUuid]);

  // ------------------------------------------------- handlers

  function handleModeChange(next: Mode): void {
    setMode(next);
    // Cancel any pending debounce — mode switches emit synchronously.
    if (oneOffTimer.current !== null) {
      clearTimeout(oneOffTimer.current);
      oneOffTimer.current = null;
    }
    if (next === 'site') {
      if (selectedSiteUuid) {
        const target = sites.find((s) => s.uuid === selectedSiteUuid);
        const enc = target?.enc_id ?? target?.uuid ?? selectedSiteUuid;
        emit({ type: 'site', site_enc_id: enc });
      } else {
        emit({ type: 'none' });
      }
    } else if (line1.trim() && postcode.trim()) {
      emit({
        type: 'one_off',
        line1: line1.trim(),
        line2: line2.trim() || null,
        city: city.trim() || null,
        postcode: postcode.trim(),
      });
    } else {
      emit({ type: 'none' });
    }
  }

  function handleSiteChange(uuid: string): void {
    setSelectedSiteUuid(uuid);
    if (uuid) {
      const target = sites.find((s) => s.uuid === uuid);
      const enc = target?.enc_id ?? target?.uuid ?? uuid;
      emit({ type: 'site', site_enc_id: enc });
    } else {
      emit({ type: 'none' });
    }
  }

  // The add-site sub-form is rendered as a <div> (not <form>) because
  // SitePicker renders inside outer pages that already own a <form>;
  // nested <form> elements are invalid HTML. The submit handler is
  // wired to the Save Site button's onClick. handleAddInputKeyDown
  // also intercepts Enter so it does not bubble to the outer form's
  // submit handler.
  function handleAddInputKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSubmit();
    }
  }

  function handleAddSubmit(): void {
    setAddError(null);
    if (!addLine1.trim() || !addPostcode.trim()) {
      setAddError('Address line 1 and postcode are required.');
      return;
    }
    createSite.mutate(
      {
        address_line1: addLine1.trim(),
        address_line2: addLine2.trim() || null,
        city: addCity.trim() || null,
        postcode: addPostcode.trim(),
        label: addLabel.trim() || undefined,
      },
      {
        onSuccess: (data) => {
          // The hook's onSuccess invalidates the list query; once the
          // refetch lands, our auto-select effect will pick up the new
          // site if we set it now. To avoid relying on the refetch
          // timing, also explicitly select it.
          setSelectedSiteUuid(data.site.uuid ?? '');
          emit({
            type: 'site',
            site_enc_id: (data.site.enc_id ?? data.site.uuid) as string,
          });
          setShowAddForm(false);
          setAddLine1('');
          setAddLine2('');
          setAddCity('');
          setAddPostcode('');
          setAddLabel('');
        },
        onError: (err) => setAddError(err.message ?? 'Could not save site'),
      },
    );
  }

  // ------------------------------------------------- render

  if (!customerEncId) {
    return (
      <div className={styles.root}>
        <div className={styles.disabled}>Select a customer first.</div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.modeRow} role="tablist" aria-label="Site mode">
        <button
          type="button"
          className={[styles.modeButton, mode === 'site' ? styles.modeButtonActive : '']
            .filter(Boolean)
            .join(' ')}
          onClick={() => handleModeChange('site')}
          role="tab"
          aria-selected={mode === 'site'}
        >
          Use saved site
        </button>
        <button
          type="button"
          className={[styles.modeButton, mode === 'one_off' ? styles.modeButtonActive : '']
            .filter(Boolean)
            .join(' ')}
          onClick={() => handleModeChange('one_off')}
          role="tab"
          aria-selected={mode === 'one_off'}
        >
          Use different address
        </button>
      </div>

      {mode === 'site' ? (
        <>
          <label className={styles.field}>
            <span className={styles.label}>Saved sites</span>
            <select
              className={styles.select}
              value={selectedSiteUuid}
              onChange={(e) => handleSiteChange(e.target.value)}
              aria-label="Saved sites"
            >
              <option value="">— Select a site —</option>
              {sites.map((s) => {
                const label = `${s.address_line1}, ${s.postcode}${s.is_primary ? ' (Primary)' : ''}`;
                return (
                  <option key={s.uuid} value={s.uuid}>
                    {label}
                  </option>
                );
              })}
            </select>
          </label>
          {!showAddForm ? (
            <button
              type="button"
              className={styles.addLink}
              onClick={() => setShowAddForm(true)}
            >
              + Add new site
            </button>
          ) : (
            <div
              className={styles.addForm}
              role="group"
              aria-label="Add new site"
            >
              <label className={[styles.field, styles.addFormFull].join(' ')}>
                <span className={styles.label}>Label (optional)</span>
                <input
                  className={styles.input}
                  value={addLabel}
                  onChange={(e) => setAddLabel(e.target.value)}
                  onKeyDown={handleAddInputKeyDown}
                  maxLength={255}
                  placeholder="e.g. Depot, Site B"
                />
              </label>
              <label className={[styles.field, styles.addFormFull].join(' ')}>
                <span className={styles.label}>Address line 1</span>
                <input
                  required
                  className={styles.input}
                  value={addLine1}
                  onChange={(e) => setAddLine1(e.target.value)}
                  onKeyDown={handleAddInputKeyDown}
                  maxLength={255}
                />
              </label>
              <label className={[styles.field, styles.addFormFull].join(' ')}>
                <span className={styles.label}>Address line 2 (optional)</span>
                <input
                  className={styles.input}
                  value={addLine2}
                  onChange={(e) => setAddLine2(e.target.value)}
                  onKeyDown={handleAddInputKeyDown}
                  maxLength={255}
                />
              </label>
              <label className={styles.field}>
                <span className={styles.label}>City (optional)</span>
                <input
                  className={styles.input}
                  value={addCity}
                  onChange={(e) => setAddCity(e.target.value)}
                  onKeyDown={handleAddInputKeyDown}
                  maxLength={100}
                />
              </label>
              <label className={styles.field}>
                <span className={styles.label}>Postcode</span>
                <input
                  required
                  className={styles.input}
                  value={addPostcode}
                  onChange={(e) => setAddPostcode(e.target.value)}
                  onKeyDown={handleAddInputKeyDown}
                  maxLength={20}
                />
              </label>
              {addError ? (
                <p className={[styles.error, styles.addFormFull].join(' ')}>
                  {addError}
                </p>
              ) : null}
              <div className={styles.addActions}>
                <Button
                  variant="ghost"
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setAddError(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  loading={createSite.isPending}
                  disabled={!addLine1.trim() || !addPostcode.trim()}
                  onClick={handleAddSubmit}
                >
                  Save Site
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className={styles.oneOff}>
          <label className={[styles.field, styles.full].join(' ')}>
            <span className={styles.label}>Address line 1</span>
            <input
              className={styles.input}
              value={line1}
              onChange={(e) => {
                const v = e.target.value;
                setLine1(v);
                emitOneOffDebounced(v, line2, city, postcode);
              }}
              maxLength={255}
              required
            />
          </label>
          <label className={[styles.field, styles.full].join(' ')}>
            <span className={styles.label}>Address line 2 (optional)</span>
            <input
              className={styles.input}
              value={line2}
              onChange={(e) => {
                const v = e.target.value;
                setLine2(v);
                emitOneOffDebounced(line1, v, city, postcode);
              }}
              maxLength={255}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>City (optional)</span>
            <input
              className={styles.input}
              value={city}
              onChange={(e) => {
                const v = e.target.value;
                setCity(v);
                emitOneOffDebounced(line1, line2, v, postcode);
              }}
              maxLength={100}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Postcode</span>
            <input
              className={styles.input}
              value={postcode}
              onChange={(e) => {
                const v = e.target.value;
                setPostcode(v);
                emitOneOffDebounced(line1, line2, city, v);
              }}
              maxLength={20}
              required
            />
          </label>
        </div>
      )}
    </div>
  );
}
